'use client'

import { useEffect, useState } from 'react'

import { clickEvent, selfServiceViewScreenEvent } from '_libs/common/event-tracker/events'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import {
  EstimatedRefundInformationModal,
  RefundEstimateModel,
  ReturnShippingOption,
  ReturnShippingOptionModel,
} from '@marketplace-web/complaints/complaints-data'

import { Screen } from 'constants/tracking/screens'
import { ClickableElement } from 'constants/tracking/clickable-elements'

import EstimatedRefundModal from './EstimatedRefundModal'
import BundleRefundsBuyerProtectionFeeModal from '../BundleRefundsBuyerProtectionFeeModal'
import ComplaintCurrencyConversionModal from '../ComplaintCurrencyConversionModal'
import ComplaintRefundsOrderSavingsModal from '../ComplaintRefundsOrderSavingsModal'

type Props = {
  note?: string
  transactionId?: number | null
  refundEstimate?: RefundEstimateModel | null
  isBundleComplaint?: boolean
  isSellerBusinessUser?: boolean
  isOpen: boolean
  selectedShippingOption?: ReturnShippingOption
  integratedReturnShippingOption?: ReturnShippingOptionModel
  isOpenedInDialog?: boolean
  onClose: () => void
}

const EstimatedRefundInformation = ({
  note,
  refundEstimate,
  transactionId,
  isOpen,
  isBundleComplaint,
  isSellerBusinessUser,
  selectedShippingOption,
  integratedReturnShippingOption,
  onClose,
  isOpenedInDialog = false,
}: Props) => {
  const { track } = useTracking()

  const [activeModal, setActiveModal] = useState<EstimatedRefundInformationModal>(
    EstimatedRefundInformationModal.EstimatedRefund,
  )

  useEffect(() => {
    if (!isOpen || !transactionId) return

    track(selfServiceViewScreenEvent({ screen: Screen.EstimatedRefundInformation, transactionId }))
  }, [track, isOpen, transactionId])

  const trackItemDiscountsInfoClick = () => {
    if (!transactionId) return

    track(
      clickEvent({
        screen: Screen.EstimatedRefundInformation,
        target: ClickableElement.ItemDiscountsRefundInformation,
        targetDetails: JSON.stringify({
          transaction_id: transactionId,
        }),
      }),
    )
  }

  const trackBuyerProtectionFeeInfoClick = () => {
    if (!transactionId) return

    track(
      clickEvent({
        screen: Screen.EstimatedRefundInformation,
        target: ClickableElement.BpfRefundInformation,
        targetDetails: JSON.stringify({
          transaction_id: transactionId,
        }),
      }),
    )
  }

  const openEstimatedRefundModal = () => {
    setActiveModal(EstimatedRefundInformationModal.EstimatedRefund)
  }

  const openBuyerProtectionFeeModal = () => {
    trackBuyerProtectionFeeInfoClick()
    setActiveModal(EstimatedRefundInformationModal.BuyerProtectionFee)
  }

  const openItemDiscountModal = () => {
    trackItemDiscountsInfoClick()
    setActiveModal(EstimatedRefundInformationModal.ItemDiscounts)
  }

  const openCurrencyConversionModal = () => {
    setActiveModal(EstimatedRefundInformationModal.CurrencyConversion)
  }

  if (!isOpen) return null

  return (
    <>
      <EstimatedRefundModal
        note={note}
        selectedShippingOption={selectedShippingOption}
        integratedReturnShippingOption={integratedReturnShippingOption}
        isBundleComplaint={isBundleComplaint}
        isOpen={activeModal === EstimatedRefundInformationModal.EstimatedRefund}
        onClose={onClose}
        refundEstimate={refundEstimate}
        isSellerBusinessUser={isSellerBusinessUser}
        onBuyerProtectionFeeInfoClick={openBuyerProtectionFeeModal}
        onCurrencyConversionInfoClick={openCurrencyConversionModal}
        onItemDiscountsInfoClick={openItemDiscountModal}
        isOpenedInDialog={isOpenedInDialog}
      />
      <ComplaintRefundsOrderSavingsModal
        isBundleComplaint={isBundleComplaint}
        isOpen={activeModal === EstimatedRefundInformationModal.ItemDiscounts}
        onClose={openEstimatedRefundModal}
        isOpenedInDialog={isOpenedInDialog}
      />
      <BundleRefundsBuyerProtectionFeeModal
        isOpen={activeModal === EstimatedRefundInformationModal.BuyerProtectionFee}
        onClose={openEstimatedRefundModal}
        isSellerBusinessUser={isSellerBusinessUser}
        isOpenedInDialog={isOpenedInDialog}
      />
      <ComplaintCurrencyConversionModal
        selectedShippingOption={selectedShippingOption}
        integratedReturnShippingOption={integratedReturnShippingOption}
        refundEstimate={refundEstimate}
        isSellerBusinessUser={isSellerBusinessUser}
        isOpen={activeModal === EstimatedRefundInformationModal.CurrencyConversion}
        onClose={openEstimatedRefundModal}
        isOpenedInDialog={isOpenedInDialog}
      />
    </>
  )
}

export default EstimatedRefundInformation
